import { TableContainer, Table, TableHead, TableBody, TableRow } from '@mui/material';
import StyledTableCell from '../../../../components/StyledTableCell';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useEnv } from '../../../../context/env-context';
import { useAuth } from '../../../../hooks/useAuth';
import { useState } from 'react';
import { makeRequest } from '../../../../utils/makeRequest';
import { TRANSFER_RECONCILE_BADGE, TRANSFER_STATUS_BADGE } from '../../../../constants/fundings';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateRight } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';

function FundingDetails({
  fundingId,
  fundingEvents,
  fundingStatus,
  getFunding,
  isConsumer,
  consumerIsTest,
  setSpinner,
}) {
  const { apiOriginFunding } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [showTransferEvents, setShowTransferEvents] = useState({});

  const toggleTransferEvent = transferId => {
    setShowTransferEvents(current => {
      if (transferId in current) {
        const copy = { ...current };

        delete copy[transferId];

        return copy;
      }

      return { ...current, [transferId]: true };
    });
  };

  const sendTransferSuccess = async transfer => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/ach_test_webhook`,
      method: 'POST',
      data: JSON.stringify({ uuid: transfer.uuid, code: 'OK', description: '' }),
    };

    try {
      await makeRequest(config);

      toast.success('Transfer Success event sent');
      getFunding(fundingId);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const sendTransferFailure = async transfer => {
    setSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/ach_test_webhook`,
      method: 'POST',
      data: JSON.stringify({
        uuid: transfer.uuid,
        code: 'ERROR',
        description: 'Test Transfer failue event',
      }),
    };

    try {
      await makeRequest(config);

      toast.success('Transfer failure event sent');
      getFunding(fundingId);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  const createManualTranfer = async event => {
    if (window.confirm('Are you sure?')) {
      setSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginFunding}/manual_transfer`,
        method: 'POST',
        data: JSON.stringify({ fundingId, topupId: event.topupId || null }),
      };

      try {
        await makeRequest(config);
        await getFunding(fundingId);

        toast.success('Manual transfer successfully created!');
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  };

  const getReconcilled = transfer => {
    if (transfer.forceReconcile && transfer.reconTransactionId) {
      return 'YES_FORCE';
    }
    if (transfer.forceReconcile) {
      return 'FORCE';
    }
    if (transfer.reconTransactionId) {
      return 'YES';
    }

    return 'NO';
  };

  const forceTransferRecon = async (transferId, force) => {
    if (window.confirm('Are you sure?')) {
      setSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginFunding}/force_transfer_reconcile`,
        method: 'POST',
        data: JSON.stringify({ transferId, force }),
      };

      try {
        await makeRequest(config);

        toast.success('Transfer force reconcile updated successfully!');
        getFunding(fundingId);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  };

  const tabapayTransferAction = async ({ url, payload, message }) => {
    if (window.confirm('Are you sure?')) {
      setSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginFunding}/${url}`,
        method: 'POST',
        data: JSON.stringify(payload),
      };

      try {
        await makeRequest(config);
        await getFunding(fundingId);

        toast.success(message);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  };

  const refresh = async () => {
    setSpinner(true);
    await getFunding(fundingId);
    setSpinner(false);
  };

  return (
    <>
      <Row className="pt-4 mb-4 ms-1">
        <Col sm="8">
          <Button className="bo-button-inline bo-button-gray" onClick={refresh}>
            <FontAwesomeIcon icon={faArrowRotateRight} size="lg" color="white" />
          </Button>
        </Col>
      </Row>
      <TableContainer>
        <Table aria-labelledby="tableTitle" aria-label="enhanced table">
          <TableHead>
            <TableRow>
              <StyledTableCell className="bo-w-220" align="left">
                Timestamp
              </StyledTableCell>
              <StyledTableCell className="bo-w-120" align="left">
                Description
              </StyledTableCell>
              <StyledTableCell className="bo-w-80" align="left">
                Amount
              </StyledTableCell>
              <StyledTableCell align="left">Reason</StyledTableCell>
              <StyledTableCell className="bo-w-600" align="left">
                Transfers
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fundingEvents.map(event => (
              <TableRow
                hover
                tabIndex={-1}
                key={event.timestamp}
                className={
                  (fundingStatus === 'OPEN' || fundingStatus === 'DUE') &&
                  (event.description === 'Open' || event.description === 'Top-up') &&
                  event.transfers.every(t => t.status === 'FAILED' || t.status === 'CANCELLED') &&
                  'bo-bg-light-red'
                }
              >
                <StyledTableCell align="left">
                  {moment.utc(event.timestamp).local().format('MMM DD, YYYY H:mm (UTCZ)')}
                </StyledTableCell>
                <StyledTableCell align="left">{event.description}</StyledTableCell>
                <StyledTableCell align="right">
                  {event.amount &&
                    `$${Number(event.amount)
                      .toLocaleString()
                      .replace(/\D\d\d$/, '')}`}
                </StyledTableCell>
                <StyledTableCell align="left">{event.reason || ''}</StyledTableCell>
                <StyledTableCell align="left">
                  <table className="bg-light">
                    <tbody>
                      {event.transfers.map(transfer => (
                        <tr key={transfer.id}>
                          <td>
                            <div className="bo-border-container w-100">
                              <table>
                                <tbody>
                                  <tr>
                                    <td className="bo-w-150">Transfer ID:</td>
                                    <td className="bo-w-250">{transfer.uuid}</td>
                                    <td className="bo-w-150">
                                      {isConsumer &&
                                        consumerIsTest &&
                                        transfer.status === 'CREATED' && (
                                          <Button
                                            className={classNames(
                                              'bo-button-inline-link',
                                              'bo-inline-badge',
                                              'm-0 p-0',
                                            )}
                                            onClick={() => sendTransferSuccess(transfer)}
                                          >
                                            Send Transfer success
                                          </Button>
                                        )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Timestamp:</td>
                                    <td>
                                      {moment
                                        .utc(transfer.createTimestamp)
                                        .local()
                                        .format('MMM DD, YYYY H:mm (UTCZ)')}
                                    </td>
                                    <td>
                                      {isConsumer &&
                                        consumerIsTest &&
                                        transfer.status === 'CREATED' && (
                                          <Button
                                            className={classNames(
                                              'bo-button-inline-link',
                                              'bo-inline-badge',
                                              'm-0 p-0',
                                            )}
                                            onClick={() => sendTransferFailure(transfer)}
                                          >
                                            Send Transfer failure
                                          </Button>
                                        )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Provider:</td>
                                    <td>{transfer.provider}</td>
                                  </tr>
                                  <tr>
                                    <td>Type:</td>
                                    <td>{transfer.type}</td>
                                  </tr>
                                  <tr>
                                    <td>Status:</td>
                                    <td className="d-flex align-items-center gap-3">
                                      {TRANSFER_STATUS_BADGE[transfer.status] && (
                                        <Badge
                                          className="bo-inline-badge"
                                          bg={TRANSFER_STATUS_BADGE[transfer.status]?.color}
                                        >
                                          {TRANSFER_STATUS_BADGE[transfer.status]?.text}
                                        </Badge>
                                      )}
                                      {transfer.provider === 'TABA_PAY' &&
                                        (transfer.type === 'ACH' ||
                                          transfer.type === 'SAME_DAY_ACH') &&
                                        (transfer.status === 'CREATED' ||
                                          transfer.status === 'COMPLETED') && (
                                          <>
                                            {transfer.status === 'CREATED' && (
                                              <Button
                                                className={classNames(
                                                  'bo-button-inline-link',
                                                  'bo-inline-badge',
                                                  'm-0 p-0',
                                                )}
                                                onClick={() =>
                                                  tabapayTransferAction({
                                                    url: 'ach_tabapay_completed',
                                                    payload: { uuid: transfer.uuid },
                                                    message: 'Transfer completed!',
                                                  })
                                                }
                                              >
                                                Complete
                                              </Button>
                                            )}
                                            <Button
                                              className={classNames(
                                                'bo-button-inline-link',
                                                'bo-inline-badge',
                                                'm-0 p-0',
                                              )}
                                              onClick={() =>
                                                tabapayTransferAction({
                                                  url: 'ach_tabapay_failed',
                                                  payload: {
                                                    uuid: transfer.uuid,
                                                    reason: 'Manual Failure by Admin',
                                                  },
                                                  message: 'Transfer failed!',
                                                })
                                              }
                                            >
                                              Fail
                                            </Button>
                                            {transfer.status === 'CREATED' && (
                                              <Button
                                                className={classNames(
                                                  'bo-button-inline-link',
                                                  'bo-inline-badge',
                                                  'm-0 p-0',
                                                )}
                                                onClick={() =>
                                                  tabapayTransferAction({
                                                    url: 'ach_tabapay_cancelled',
                                                    payload: { uuid: transfer.uuid },
                                                    message: 'Transfer cancelled!',
                                                  })
                                                }
                                              >
                                                Cancel
                                              </Button>
                                            )}
                                          </>
                                        )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Fee:</td>
                                    <td>{transfer.fee || 'No Fee'}</td>
                                  </tr>
                                  <tr>
                                    <td>Failure Reason:</td>
                                    <td>{transfer.failureReason}</td>
                                  </tr>
                                  <tr>
                                    <td>Reconcilled:</td>
                                    <td>
                                      <Badge
                                        className="bo-inline-badge"
                                        bg={
                                          TRANSFER_RECONCILE_BADGE[getReconcilled(transfer)]?.color
                                        }
                                      >
                                        {TRANSFER_RECONCILE_BADGE[getReconcilled(transfer)]?.text}
                                      </Badge>
                                    </td>
                                    <td>
                                      <Button
                                        className="bo-button-inline-link bo-inline-badge m-0 p-0"
                                        onClick={() =>
                                          forceTransferRecon(transfer.id, !transfer.forceReconcile)
                                        }
                                      >
                                        {transfer.forceReconcile
                                          ? 'Cancel Force'
                                          : 'Force Reconcile'}
                                      </Button>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                              <Row className="ms-0 mt-1">
                                <Button
                                  className={classNames(
                                    'bo-button-inline-link',
                                    'bo-inline-badge',
                                    'text-start m-0 p-0',
                                  )}
                                  onClick={() => toggleTransferEvent(transfer.id)}
                                >
                                  {transfer.id in showTransferEvents
                                    ? 'Hide events'
                                    : 'Show events'}
                                </Button>
                              </Row>
                              {transfer.id in showTransferEvents && (
                                <Row className="ms-1 mt-1">
                                  <table>
                                    <thead>
                                      <tr>
                                        <th className="bo-w-200">Timestamp</th>
                                        <th>Description</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {transfer.events.map(tEvent => (
                                        <tr key={tEvent.id}>
                                          <td>
                                            {moment
                                              .utc(tEvent.timestamp)
                                              .local()
                                              .format('MMM DD, YYYY H:mm (UTCZ)')}
                                          </td>
                                          <td>{tEvent.description}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </Row>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(fundingStatus === 'OPEN' || fundingStatus === 'DUE') &&
                    (event.description === 'Open' || event.description === 'Top-up') &&
                    event.transfers.every(
                      t => t.status === 'FAILED' || t.status === 'CANCELLED',
                    ) && (
                      <Button
                        className="bo-common-button mt-2"
                        onClick={() => createManualTranfer(event)}
                      >
                        Create Manual Transfer
                      </Button>
                    )}
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default memo(FundingDetails);
